import React, { useState } from 'react';
import { Mail, Send, CheckCircle2, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactForm = ({ onClose }) => {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState({ loading: false, success: false, error: null });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, success: false, error: null });

    try {
      const res = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'שגיאה בשליחת ההודעה');
      setStatus({ loading: false, success: true, error: null });
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      setStatus({ loading: false, success: false, error: err.message });
    }
  };

  if (status.success) {
    return (
      <div className="text-center py-8" data-testid="contact-success">
        <CheckCircle2 className="w-16 h-16 text-teal-500 mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-slate-900 mb-2">ההודעה נשלחה! 🎉</h3>
        <p className="text-slate-600 mb-6">
          תודה שפנית אלינו. קיבלת מייל אישור, ונחזור אליך בהקדם האפשרי (בדרך כלל תוך 24 שעות).
        </p>
        {onClose && (
          <button
            onClick={onClose}
            className="bg-teal-500 hover:bg-teal-600 text-white px-6 py-2 rounded-full font-bold"
            data-testid="contact-success-close-btn"
          >
            סגור
          </button>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form" dir="rtl">
      <div className="flex items-center gap-2 mb-2">
        <Mail className="w-6 h-6 text-teal-500" />
        <h3 className="text-2xl font-bold text-slate-900">צור קשר</h3>
      </div>
      <p className="text-slate-500 text-sm mb-6">
        יש שאלה, הצעה, או בעיה טכנית? נשמח לשמוע ממך. נחזור אליך בתוך 24 שעות.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-bold text-slate-700 mb-1">שם מלא *</label>
          <input
            type="text"
            name="name"
            value={form.name}
            onChange={handleChange}
            required
            minLength={2}
            className="w-full px-4 py-2 rounded-xl border border-slate-200 focus:border-teal-500 focus:outline-none"
            placeholder="השם שלך"
            data-testid="contact-name-input"
          />
        </div>
        <div>
          <label className="block text-sm font-bold text-slate-700 mb-1">מייל *</label>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            required
            className="w-full px-4 py-2 rounded-xl border border-slate-200 focus:border-teal-500 focus:outline-none"
            placeholder="your@email.com"
            data-testid="contact-email-input"
            dir="ltr"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-bold text-slate-700 mb-1">נושא</label>
        <input
          type="text"
          name="subject"
          value={form.subject}
          onChange={handleChange}
          className="w-full px-4 py-2 rounded-xl border border-slate-200 focus:border-teal-500 focus:outline-none"
          placeholder="לדוגמה: בעיה טכנית, שאלה על מנוי, הצעה לשיפור..."
          data-testid="contact-subject-input"
        />
      </div>

      <div>
        <label className="block text-sm font-bold text-slate-700 mb-1">הודעה *</label>
        <textarea
          name="message"
          value={form.message}
          onChange={handleChange}
          required
          minLength={5}
          maxLength={5000}
          rows={6}
          className="w-full px-4 py-2 rounded-xl border border-slate-200 focus:border-teal-500 focus:outline-none resize-y"
          placeholder="ספרי לנו במה נוכל לעזור..."
          data-testid="contact-message-input"
        />
        <div className="text-xs text-slate-400 mt-1 text-left">
          {form.message.length}/5000
        </div>
      </div>

      {status.error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-xl text-sm" data-testid="contact-error">
          ❌ {status.error}
        </div>
      )}

      <button
        type="submit"
        disabled={status.loading}
        className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white font-bold py-3 rounded-full transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        data-testid="contact-submit-btn"
      >
        {status.loading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            שולח...
          </>
        ) : (
          <>
            <Send className="w-5 h-5" />
            שלח הודעה
          </>
        )}
      </button>

      <p className="text-xs text-slate-400 text-center pt-2">
        ההודעה תישלח ל-support@ah-online.com ותקבלי אישור במייל.
      </p>
    </form>
  );
};

export default ContactForm;
